// ============================================================================
// Rota · Palco (hospedagem, player e métricas das VSLs) · contratos v1 · 24/10/2026
// Fonte única dos formatos trocados entre o player (packages/player), o servidor (apps/palco) e a
// área Palco do painel (apps/web/src/areas/palco). Especificação: docs/palco/SPEC.md.
// ============================================================================

export const PALCO_CONTRACT_VERSION = 1 as const;

/** Id de vídeo: 12 caracteres [a-z0-9]. Também é o id do player no embed. */
export type PalcoVideoId = string;

export type PalcoVideoStatus =
  | 'uploading' // recebendo os pedaços do arquivo
  | 'queued' // arquivo completo, esperando a conversão
  | 'processing' // ffmpeg convertendo para HLS
  | 'ready' // pronto para tocar
  | 'failed'; // conversão falhou (ver `error`)

export type PalcoAspect = '16:9' | '9:16' | '1:1' | '4:5';

export interface PalcoCta {
  id: string;
  /** Segundo do vídeo em que o botão aparece. */
  atSec: number;
  /** Some de novo neste segundo (null = fica até o fim). */
  untilSec: number | null;
  text: string;
  url: string;
  newTab: boolean;
  /** Embaixo do vídeo (na página) ou por cima do vídeo. */
  position: 'below' | 'over';
}

/** Elemento da página (fora do player) que aparece num segundo do vídeo, como a oferta. */
export interface PalcoDelayedElement {
  /** Seletor CSS na página do cliente (ex.: ".oferta" ou "#offer-content"). */
  selector: string;
  atSec: number;
  /** Lembra no navegador: quem já chegou nesse ponto vê o elemento ao voltar. */
  persist: boolean;
}

export interface PalcoChapter {
  atSec: number;
  title: string;
}

/** Id de um arquivo enviado para o vídeo (imagem ou legenda): 12 caracteres [a-z0-9]. */
export type PalcoAssetId = string;

/** Os 6 modelos do botão de play que fica por cima do vídeo. */
export type PalcoPlayButtonStyle = 'circle' | 'rounded' | 'pill' | 'outline' | 'pulse' | 'glass';

export interface PalcoPlayButton {
  style: PalcoPlayButtonStyle;
  /** Tamanho em px (40 a 160) no player com 640 px de largura; escala com o player. */
  size: number;
  /** Cor do fundo do botão (hex); null = cor do player. */
  color: string | null;
  /** Cor do ícone/texto (hex). */
  iconColor: string;
  /** Texto ao lado do ícone (usado no modelo 'pill'; vazio = sem texto). */
  label: string;
  /** Imagem (PNG/WebP/GIF) no lugar do botão; quando existe, os campos acima são ignorados. */
  imageAssetId: PalcoAssetId | null;
}

/** ThumbSniper: imagens por cima do vídeo quando a pessoa pausa ou quando ele termina. */
export interface PalcoThumbSniper {
  enabled: boolean;
  /** Pausou antes do pitch (retenção). */
  pauseImageAssetId: PalcoAssetId | null;
  /** Pausou depois do pitch (recuperação), com botão opcional para o checkout. */
  pauseAfterPitchImageAssetId: PalcoAssetId | null;
  pauseAfterPitchCta: { text: string; url: string } | null;
  /** Vídeo terminou. */
  endImageAssetId: PalcoAssetId | null;
}

/** Mini-gancho: frase (ou imagem) por cima do vídeo num trecho, para segurar quem ia sair. */
export interface PalcoMiniHook {
  id: string;
  atSec: number;
  durationSec: number;
  text: string;
  imageAssetId: PalcoAssetId | null;
  position: 'top' | 'center' | 'bottom';
  style: 'bold' | 'callout' | 'highlight';
}

/** Filtro de tráfego: quem não passa nas regras vê o vídeo seguro (ou uma mensagem). */
export interface PalcoTrafficFilter {
  enabled: boolean;
  /** Aparelhos liberados (vazio = todos). */
  devices: PalcoDevice[];
  /** Prefixos de idioma do navegador liberados, ex.: ["pt"] (vazio = todos). */
  languages: string[];
  /** Países liberados (ISO-2, ex.: "BR"), estimados pelo fuso horário do navegador (vazio = todos). */
  countries: string[];
  /** Exige um parâmetro na URL da página, ex.: { key: "utm_source", value: "facebook" } (value vazio = só existir). */
  requireParam: { key: string; value: string } | null;
  /** Bloqueia robôs e revisores de anúncio conhecidos pelo navegador (user agent). */
  blockBots: boolean;
  fallback: 'safeVideo' | 'message';
  safeVideoId: PalcoVideoId | null;
  message: string;
}

export interface PalcoSettings {
  /** Cor de destaque do player (hex #rrggbb). */
  color: string;
  aspect: PalcoAspect;
  /**
   * smart = começa sozinho sem som, com o aviso "Seu vídeo já começou / Clique para ouvir";
   * click = espera o clique no play.
   */
  autoplay: 'smart' | 'click';
  smartOverlay: {
    title: string;
    subtitle: string;
    /** Ao tocar no aviso, volta para o 0 com som (comportamento clássico de VSL). */
    restartOnUnmute: boolean;
  };
  /** fake = barra que corre mais rápido no começo e devagar no fim; real; hidden = sem barra. */
  progressBar: 'fake' | 'real' | 'hidden';
  /** Força da barra rápida: 0 (= real) a 1 (bem acelerada no começo). */
  fakeProgressStrength: number;
  /**
   * Curva da barra rápida. threePhase (PalcoTube): 0–30% do vídeo enche 50% da barra, 30–70% vai de
   * 50% a 85% e 70–100% de 85% a 100%. smooth: 1 − (1 − x)^(1 + 3·força).
   */
  progressCurve: 'threePhase' | 'smooth';
  /** Altura da barra em px (2 a 20). */
  progressHeight: number;
  progressPosition: 'bottom' | 'top';
  controls: {
    playPause: boolean;
    /** Deixa arrastar a barra (VSL normalmente não deixa). */
    seek: boolean;
    volume: boolean;
    fullscreen: boolean;
    speed: boolean;
  };
  resume: {
    enabled: boolean;
    title: string;
    continueLabel: string;
    restartLabel: string;
  };
  pauseScreen: { enabled: boolean; text: string };
  endScreen: { mode: 'none' | 'replay' | 'loop'; text: string };
  /** Pede o modo "imagem dentro da imagem" ao rolar para fora (mini-player no canto). */
  miniPlayer: boolean;
  ctas: PalcoCta[];
  delayedElements: PalcoDelayedElement[];
  chapters: PalcoChapter[];
  /** Segundo em que começa a oferta (marca no gráfico de retenção e métrica "chegada ao pitch"). */
  pitchAtSec: number | null;
  tracking: {
    /** Dispara eventos no Meta Pixel da página (fbq), se existir. */
    metaPixel: boolean;
    /** Dispara eventos no GA4 da página (gtag), se existir. */
    ga4: boolean;
    /** Repassa utm_*, src e sck da página para os links dos CTAs (checkout). */
    passUtms: boolean;
  };
  /** Domínios onde o embed pode tocar (vazio = qualquer um). Ex.: ["aula.zeroeua.com.br"]. */
  allowedDomains: string[];
  /** Teste A/B: quando ativo, o embed sorteia (fixo por visitante) um destes vídeos. */
  abTest: { enabled: boolean; variants: { videoId: PalcoVideoId; weight: number }[] } | null;
  playButton: PalcoPlayButton;
  thumbSniper: PalcoThumbSniper;
  miniHooks: PalcoMiniHook[];
  /** Turbo: toca um pouco mais rápido sem mostrar (1,0 a 1,5). */
  turbo: { enabled: boolean; speed: number };
  /** Headline acima do vídeo (texto e/ou imagem). */
  headline: {
    enabled: boolean;
    text: string;
    subtext: string;
    imageAssetId: PalcoAssetId | null;
    /** Cor do texto (hex); null = herda da página. */
    color: string | null;
    align: 'left' | 'center';
  };
  trafficFilter: PalcoTrafficFilter;
  /** Legenda .vtt enviada pelo dono. */
  subtitles: {
    enabled: boolean;
    trackAssetId: PalcoAssetId | null;
    label: string;
    showByDefault: boolean;
    size: 'sm' | 'md' | 'lg';
    background: boolean;
  };
  /**
   * Eventos no Pixel da Meta / GA4 por quanto a pessoa assistiu com som: porcentagens (ex.: 25, 50,
   * 75, 100) e segundos exatos. Nome do evento: `${eventPrefix}${n}` (ex.: PalcoTube50, PalcoTube60s).
   */
  pixelEvents: { percents: number[]; atSeconds: number[]; eventPrefix: string };
  /** Senha no vídeo (a senha em si é gravada por PUT /api/palco/videos/:id/password, nunca aqui). */
  password: { enabled: boolean; hint: string };
}

export interface PalcoRendition {
  height: number; // 360, 540, 720, 1080
  bitrateKbps: number;
}

export interface PalcoVideoSummary {
  id: PalcoVideoId;
  title: string;
  status: PalcoVideoStatus;
  durationSec: number | null;
  posterUrl: string | null;
  createdAt: number; // epoch ms
  updatedAt: number;
  /** Últimos 7 dias, para a lista. */
  stats7d: { loads: number; plays: number; playRate: number; pitchReach: number | null };
  /** Pessoas assistindo agora (batida nos últimos 30 s). */
  liveViewers: number;
  /** Pasta (null = fora de pasta). */
  folderId: string | null;
  /** Na lixeira desde (null = ativo). Some sozinho 30 dias depois. */
  deletedAt: number | null;
  /** 0..1 durante o envio ou a conversão (null fora disso). */
  progress: number | null;
}

export interface PalcoVideoDetail extends PalcoVideoSummary {
  settings: PalcoSettings;
  /** 0..1 durante o upload ou a conversão. */
  progress: number | null;
  error: string | null;
  sizeBytes: number | null;
  width: number | null;
  height: number | null;
  renditions: PalcoRendition[];
  /** URL pública do HLS (master.m3u8). */
  hlsUrl: string | null;
  /** Código para colar na página. */
  embedHtml: string;
  /** Imagens e legendas enviadas para este vídeo. */
  assets: PalcoAsset[];
  /** Senha gravada (o valor nunca volta). */
  hasPassword: boolean;
  storyboard: PalcoStoryboard | null;
}

export interface PalcoAsset {
  id: PalcoAssetId;
  kind: 'image' | 'subtitle';
  name: string;
  mime: string;
  sizeBytes: number;
  /** URL pública (/p/v/<videoId>/a/<assetId>.<ext>). */
  url: string;
  createdAt: number;
}

/**
 * Quadros do vídeo para o gráfico (passar o mouse mostra o quadro exato): folhas JPEG com
 * `columns × rows` quadros de `tileWidth × tileHeight`, um a cada `intervalSec`. O quadro do segundo
 * t é i = floor(t / intervalSec): folha floor(i / (columns·rows)), posição i mod (columns·rows),
 * linha a linha.
 */
export interface PalcoStoryboard {
  intervalSec: number;
  tileWidth: number;
  tileHeight: number;
  columns: number;
  rows: number;
  frameCount: number;
  sheets: string[];
}

export interface PalcoFolder {
  id: string;
  name: string;
  videoCount: number;
  createdAt: number;
}

/** PUT /api/palco/videos/:id/password: null tira a senha. */
export interface PalcoPasswordRequest {
  password: string | null;
}

/** Métrica criada pelo dono a partir das métricas do vídeo (conta feita no painel). */
export type PalcoMetricKey = keyof PalcoTotals;
export interface PalcoCustomMetric {
  id: string;
  name: string;
  a: PalcoMetricKey;
  op: 'div' | 'mul' | 'sub' | 'add';
  /** Outra métrica ou um número fixo. */
  b: PalcoMetricKey | number;
  format: 'number' | 'percent' | 'currency';
}

/** GET /api/palco/abtests: cada embed com teste A/B ligado, com as variantes lado a lado. */
export interface PalcoAbTestSummary {
  embedId: PalcoVideoId;
  title: string;
  status: 'running' | 'winner' | 'stopped';
  startedAt: number | null;
  /** Métrica que decide: vendas se houver, senão cliques no botão, senão chegada ao pitch. */
  metric: 'conversions' | 'ctaClicks' | 'pitchReach';
  variants: {
    videoId: PalcoVideoId;
    title: string;
    posterUrl: string | null;
    weight: number;
    loads: number;
    plays: number;
    unmutes: number;
    playRate: number;
    pitchReach: number | null;
    ctaClicks: number;
    ctaRate: number | null;
    conversions: number;
    /** Taxa da métrica que decide (por play com som). */
    rate: number;
  }[];
  winnerVideoId: PalcoVideoId | null;
  /** Confiança do vencedor (0..1, teste de duas proporções; vencedor só com ≥ 0,95 e 100 plays cada). */
  confidence: number | null;
}

export interface PalcoCreateVideoRequest {
  title: string;
  fileName: string;
  sizeBytes: number;
}
export interface PalcoCreateVideoResponse {
  id: PalcoVideoId;
  /** PUT de pedaços em sequência: header `Content-Range: bytes start-end/total`. */
  uploadUrl: string;
  chunkBytes: number;
}

export interface PalcoUploadChunkResponse {
  receivedBytes: number;
}

export interface PalcoUpdateVideoRequest {
  title?: string;
  folderId?: string | null;
  /** Parcial: os campos enviados substituem os atuais (objetos aninhados são mesclados). */
  settings?: Partial<PalcoSettings>;
}

/** PUT /api/palco/order: a ordem da lista de vídeos (o dono arrasta os cartões). Todos os ids. */
export interface PalcoReorderRequest {
  ids: PalcoVideoId[];
}

// ---------- métricas ----------

export interface PalcoTotals {
  /** Carregamentos do player (página aberta com o vídeo). */
  loads: number;
  /** Sessões que tocaram (inclui autoplay sem som). */
  plays: number;
  /** Sessões em que a pessoa ativou o som (clicou no aviso ou no volume). */
  unmutes: number;
  /** plays ÷ loads. */
  playRate: number;
  /** unmutes ÷ plays (quem realmente começou a assistir). */
  engagementRate: number;
  uniqueViewers: number;
  avgWatchSec: number;
  /** Fração de quem ativou o som que chegou ao `pitchAtSec` (null sem pitch configurado). */
  pitchReach: number | null;
  /** Fração de quem ativou o som que chegou ao fim. */
  completionRate: number;
  ctaClicks: number;
  /** ctaClicks ÷ quem chegou ao primeiro CTA. */
  ctaRate: number | null;
  /** Vendas atribuídas (webhook do checkout com sck da sessão); fase 2. */
  conversions: number;
  revenueCents: number;
  /** Visitantes únicos que carregaram e que deram play (uniquePlays = uniqueViewers). */
  uniqueLoads: number;
  uniquePlays: number;
  /** Quantas sessões com som chegaram ao pitch (o número, não a fração). */
  pitchAudience: number;
  /** Tempo médio assistido ÷ duração. */
  engagement: number;
  /** conversions ÷ plays com som. */
  conversionRate: number;
}

export interface PalcoBreakdownRow {
  key: string; // ex.: "facebook / cpc / anuncio-12", "mobile", "BR"
  loads: number;
  plays: number;
  unmutes: number;
  playRate: number;
  pitchReach: number | null;
  ctaClicks: number;
  conversions: number;
}

export interface PalcoAnalytics {
  videoId: PalcoVideoId;
  from: number;
  to: number;
  durationSec: number;
  pitchAtSec: number | null;
  totals: PalcoTotals;
  /**
   * Retenção por segundo: retention[s] = fração das sessões COM SOM que assistiram o segundo s
   * (índice 0..durationSec-1). Base: unmutes.
   */
  retention: number[];
  /** Mesma curva para quem não ativou o som (autoplay mudo), para ver a perda no aviso. */
  retentionMuted: number[];
  /** Série diária (dia em America/Sao_Paulo, 'YYYY-MM-DD'). */
  daily: {
    day: string;
    loads: number;
    plays: number;
    unmutes: number;
    ctaClicks: number;
    conversions: number;
  }[];
  bySource: PalcoBreakdownRow[]; // utm_source / utm_medium / utm_campaign
  byContent: PalcoBreakdownRow[]; // utm_content (anúncio)
  byDevice: PalcoBreakdownRow[]; // mobile, desktop, tablet
  liveViewers: number;
  /** Pessoas (com som) assistindo cada segundo: audience[s] = retention[s] × unmutes. */
  audience: number[];
  /** Quantas sessões reassistiram cada segundo (voltaram a barra, repetiram). */
  rewatch: number[];
  byCountry: PalcoBreakdownRow[]; // ISO-2, estimado pelo fuso do navegador
  byOs: PalcoBreakdownRow[];
  byBrowser: PalcoBreakdownRow[];
  /** Melhores horários: plays por dia da semana (0 = domingo) × hora (0..23), horário de Brasília. */
  bestTimes: number[][];
  funnel: {
    loads: number;
    plays: number;
    unmutes: number;
    pitch: number;
    ctaClicks: number;
    conversions: number;
  };
  storyboard: PalcoStoryboard | null;
}

export interface PalcoLiveResponse {
  at: number;
  total: number;
  byVideo: { videoId: PalcoVideoId; viewers: number }[];
}

// ---------- coleta (player → servidor), POST /p/t (text/plain com JSON, via sendBeacon) ----------

export type PalcoDevice = 'mobile' | 'tablet' | 'desktop';

export type PalcoBeaconEvent =
  | {
      t: 'load';
      ts: number;
      url: string;
      ref: string;
      utm: { source?: string; medium?: string; campaign?: string; content?: string; term?: string };
      src?: string;
      sck?: string;
      dev: PalcoDevice;
      lang: string;
      /** Fuso horário do navegador (Intl), para estimar o país. */
      tz?: string;
    }
  | { t: 'play'; ts: number; at: number; muted: boolean; auto: boolean }
  | { t: 'unmute'; ts: number; at: number }
  /** Trechos assistidos desde a última batida, em segundos inteiros [início, fim). */
  | { t: 'ranges'; ts: number; muted: boolean; r: [number, number][] }
  | { t: 'pause'; ts: number; at: number }
  | { t: 'end'; ts: number }
  | { t: 'cta'; ts: number; id: string; at: number }
  | { t: 'resume'; ts: number; choice: 'continue' | 'restart'; at: number }
  | { t: 'hb'; ts: number; at: number }
  /** Trechos REassistidos (com som) desde a última batida: voltou a barra ou repetiu. */
  | { t: 'rw'; ts: number; r: [number, number][] };

export interface PalcoBeacon {
  /** Versão do protocolo. */
  p: 1;
  /** Vídeo efetivamente tocado (no teste A/B, a variante sorteada). */
  v: PalcoVideoId;
  /** Id do embed (o vídeo do código colado na página). */
  e: PalcoVideoId;
  /** Sessão: um carregamento do player (aleatório, 16+ caracteres). */
  s: string;
  /** Visitante: guardado no localStorage do domínio da página (aleatório). */
  u: string;
  ev: PalcoBeaconEvent[];
}

/** Configuração pública entregue ao player pelo embed (sem nada interno). */
export interface PalcoPublicConfig {
  embedId: PalcoVideoId;
  videoId: PalcoVideoId;
  title: string;
  durationSec: number;
  hlsUrl: string;
  posterUrl: string | null;
  /** MP4 de reserva (360p) para navegadores sem HLS nem MSE. */
  fallbackMp4Url: string | null;
  settings: PalcoSettings;
  /** Endpoint de coleta. */
  beaconUrl: string;
  /** O vídeo tem faixa de áudio (sem áudio, o início automático não mostra "Clique para ouvir"). */
  hasAudio?: boolean;
  /** URL pública de cada arquivo usado nos ajustes (botão, ThumbSniper, mini-ganchos, headline, legenda). */
  assets: Record<PalcoAssetId, string>;
  /** Vídeo com senha: hlsUrl/fallbackMp4Url vêm vazios até o player liberar em `unlockUrl`. */
  locked?: boolean;
  /** POST { password } → 200 { hlsUrl, fallbackMp4Url } ou 403. */
  unlockUrl?: string;
  /** Config do vídeo seguro do filtro de tráfego (já pronta para tocar). */
  safeConfig?: PalcoPublicConfig;
  /**
   * Teste A/B ativo: a configuração pronta de cada variante (incluindo o vídeo principal, se ele
   * estiver no teste). O player sorteia uma, fixa por visitante, e toca com o mesmo embedId.
   */
  variants?: { config: PalcoPublicConfig; weight: number }[];
}

export function defaultPalcoSettings(): PalcoSettings {
  return {
    color: '#e11d48',
    aspect: '16:9',
    autoplay: 'smart',
    smartOverlay: {
      title: 'Seu vídeo já começou',
      subtitle: 'Clique para ouvir',
      restartOnUnmute: true,
    },
    progressBar: 'fake',
    fakeProgressStrength: 0.6,
    progressCurve: 'threePhase',
    progressHeight: 6,
    progressPosition: 'bottom',
    controls: { playPause: true, seek: false, volume: true, fullscreen: true, speed: false },
    resume: {
      enabled: true,
      title: 'Você já começou a assistir este vídeo',
      continueLabel: 'Continuar assistindo',
      restartLabel: 'Assistir do início',
    },
    pauseScreen: { enabled: true, text: 'Vídeo pausado. Clique para continuar' },
    endScreen: { mode: 'replay', text: 'Assistir de novo' },
    miniPlayer: false,
    ctas: [],
    delayedElements: [],
    chapters: [],
    pitchAtSec: null,
    tracking: { metaPixel: true, ga4: false, passUtms: true },
    allowedDomains: [],
    abTest: null,
    playButton: {
      style: 'circle',
      size: 84,
      color: null,
      iconColor: '#ffffff',
      label: 'Assistir',
      imageAssetId: null,
    },
    thumbSniper: {
      enabled: false,
      pauseImageAssetId: null,
      pauseAfterPitchImageAssetId: null,
      pauseAfterPitchCta: null,
      endImageAssetId: null,
    },
    miniHooks: [],
    turbo: { enabled: false, speed: 1.1 },
    headline: { enabled: false, text: '', subtext: '', imageAssetId: null, color: null, align: 'center' },
    trafficFilter: {
      enabled: false,
      devices: [],
      languages: [],
      countries: [],
      requireParam: null,
      blockBots: true,
      fallback: 'message',
      safeVideoId: null,
      message: 'Este vídeo não está disponível.',
    },
    subtitles: {
      enabled: false,
      trackAssetId: null,
      label: 'Português',
      showByDefault: true,
      size: 'md',
      background: true,
    },
    pixelEvents: { percents: [25, 50, 75, 100], atSeconds: [], eventPrefix: 'PalcoTube' },
    password: { enabled: false, hint: '' },
  };
}
